import React, { useMemo, useRef, useState } from "react";
import { Flex } from "hcss-core";
import { Icon } from "hcss-components";
import { EmailField, formatFirstNameLastName, TextOverflow } from "core";
import { useSelector } from "react-redux";
import { selectors as contactsSelectors } from "modules/contacts";
import { PhoneNumber } from "modules/contacts/components/vendor-details";
import { usePermissions, useAuthorization } from "modules/account";
import { DropdownList } from "react-widgets";
import { SchemaFormFieldInputProps } from "./common";
import { strings } from "localization";
import styled from "styled-components";
import { UserAvatar } from "../../../../core/components/avatar";
import { Overlay } from "hcss-components";
import { ContactPopover } from "modules/contacts/components/contact-popover";
import { intersection, sortBy } from "lodash-es";
import { IContactProduct } from "api/GeneratedClients/ContactsClient";

export interface ContactFieldData {
  id: string | undefined;
  firstName: string | undefined;
  lastName: string | undefined;
  companyId: string;
  companyCode: string;
  companyName: string | undefined;
  email: string | undefined;
  phoneNumber: string | undefined;
  products: IContactProduct[] | undefined;
}
export interface ContactListOption {
  display: string;
  value: ContactFieldData | null;
}
export const ContactInput = (props: SchemaFormFieldInputProps) => {
  const {
    form,
    field,
    schemaField: { adminOnly, config },
    isLocked
  } = props;
  const [contact, setContact] = useState<ContactFieldData | null>(field.value);
  const vendorDataOptions = useSelector(
    contactsSelectors.getVendorListSortedByCode
  );

  let options: ContactListOption[] = useMemo(() => {
    let options: ContactListOption[] = [];
    vendorDataOptions.forEach(vendor => {
      const contacts = vendor.contacts.map(contact => {
        const fullname = formatFirstNameLastName(
          contact.firstName,
          contact.lastName
        );
        return {
          display: fullname,
          value: {
            id: contact.id,
            firstName: contact.firstName,
            lastName: contact.lastName,
            companyId: vendor.id,
            companyCode: vendor.code,
            companyName: vendor.name,
            email: contact.emailAddress,
            phoneNumber: contact.phoneNumber,
            products: contact.products,
            office: vendor.locations?.find(
              l => l.id === contact?.vendorLocationId
            )
          },
          id: contact.id
        };
      });
      options = options.concat(contacts);
    });
    return options;
  }, [vendorDataOptions]);

  if (config.filterEnabled && config?.filters.length > 0) {
    options = options.filter(op => {
      if (!op.value?.products || op.value?.products.length === 0) return false;
      return (
        intersection(
          config?.filters,
          op.value?.products.map((p: any) => p.productTypeId)
        ).length > 0
      );
    });
  }

  options = sortBy(options, [
    c => c.value?.companyCode?.toLowerCase(),
    c => c.value?.firstName?.toLowerCase(),
    c => c.value?.lastName?.toLowerCase()
  ]);

  const permissions = usePermissions();
  const authorization = useAuthorization();
  const adminFailure = !permissions.admin && adminOnly;
  const fieldIsEditable =
    permissions.write && authorization.canAccessLimitedPrecon && !adminFailure;

  if (field.value) {
    options.push({ display: "(clear)", value: null });
  }

  const handleChange = (data: ContactListOption) => {
    setContact(data.value);
    form.setFieldValue(field.name, data.value);
  };

  const fullname = formatFirstNameLastName(
    contact?.firstName,
    contact?.lastName
  );

  return (
    <Flex alignItems="center" justifyContent="flex-start">
      <UserAvatar
        name={fullname}
        id={contact?.id ? contact.id : ""}
        size="57.5px"
      />
      <Flex
        flexDirection="column"
        marginLeft="12px"
        color="rgb(64, 64, 64)"
        style={{ width: "100%", zIndex: "999", lineHeight: "2rem" }}
      >
        <Flex fontSize="1.6rem" fontWeight={600}>
          <TextOverflow style={{ maxWidth: "80%", whiteSpace: "nowrap" }}>
            {fullname ? fullname : "-"}
          </TextOverflow>
        </Flex>
        <Flex fontSize="1.3rem">
          <TextOverflow style={{ maxWidth: "80%", whiteSpace: "nowrap" }}>
            {contact && contact.companyName ? contact.companyName : ""}
          </TextOverflow>
        </Flex>
        {contact?.email && (
          <Flex style={{ maxWidth: "80%" }}>
            <StyledIcon name="envelope" />
            <div style={{ overflow: "hidden" }}>
              <EmailField emailAddress={contact.email} />
            </div>
          </Flex>
        )}
        {contact?.phoneNumber && (
          <Flex style={{ maxWidth: "80%" }}>
            <div style={{ overflow: "hidden" }}>
              <PhoneNumber phoneNumber={contact.phoneNumber} />
            </div>
          </Flex>
        )}
      </Flex>
      {fieldIsEditable && !isLocked && (
        <div id="arrow-dropdown-container">
          <DropdownContainer
            hasErrors={
              form.errors[field.name] && form.touched[field.name] ? true : false
            }
          >
            <DropdownList
              filter={(option, search) => ContactDropdownFilter(option, search)}
              textField="display"
              valueField="id"
              value={contact}
              data={options}
              onChange={handleChange}
              id="arrow-dropdown"
              messages={{
                emptyList: strings.projects.setup.message.emptyContactList
              }}
              itemComponent={({ item }) => <ContactOption {...item.value} />}
            />
          </DropdownContainer>
        </div>
      )}
    </Flex>
  );
};

export const ContactOption = (props: ContactFieldData) => {
  const { companyCode, companyName, firstName, lastName, email, id } = props;
  const fullname = formatFirstNameLastName(firstName, lastName);
  const optionRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  if (!props.id) return <div>{`${"(clear)"}`}</div>;
  return (
    <OptionContainer
      ref={optionRef}
      onMouseEnter={() => {
        setShow(true);
      }}
      onMouseLeave={() => setShow(false)}
      onClick={e => {
        setShow(false);
      }}
    >
      <OptionHeader>
        <TextOverflow className="option-name">{fullname}</TextOverflow>
        <TextOverflow className="option-code">{companyCode}</TextOverflow>
      </OptionHeader>
      <Overlay
        rootClose
        show={show}
        placement="right"
        target={optionRef.current ?? undefined}
      >
        <ContactPopover
          name={fullname}
          email={email}
          id={id}
          companyName={companyName}
          setShow={setShow}
        />
      </Overlay>
    </OptionContainer>
  );
};

export const ContactDropdownFilter = (
  { value }: ContactListOption,
  search: string
) => {
  if (!value) return false;
  return (
    value.companyCode.toLowerCase().includes(search.toLowerCase()) ||
    (value.companyName
      ? value.companyName.toLowerCase().includes(search.toLowerCase())
      : false) ||
    (value.firstName
      ? value.firstName.toLowerCase().includes(search.toLowerCase())
      : false) ||
    (value.lastName
      ? value.lastName.toLowerCase().includes(search.toLowerCase())
      : false)
  );
};
const DropdownContainer = styled.div<{ hasErrors?: boolean }>`
  & .rw-widget-input {
    height: 34px;
    ${props => (props.hasErrors ? "border: 1px solid #a94442;" : "")};
  }
}
`;

const OptionContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionHeader = styled.div`
  display: flex;

  .option-name {
    white-space: nowrap;
    flex: 2;
    font-size: 13px;
    font-weight: 700;
    margin-right: 3px;
  }

  .option-code {
    white-space: nowrap;
    flex: 1;
    font-size: 13px;
  }
`;

const StyledIcon = styled(Icon)`
  margin-right: 1rem;
  font-size: 1.5rem;
  width: 1em;
`;
